import { fetchMyBrandAccess } from '@owner/auth'

import { useAuthStore } from '@/auth/useAuthStore'
import { logError, logInfo } from '@/logger'
import { FeatureFlags } from '@/plugins/featureFlags'

import { type HermesGuardFn } from './types'

const AUTH_SERVICE_URL = import.meta.env.VITE_AUTH_SERVICE_URL

/** Kill switch / gradual-rollout flag for the brand-access guard. */
export const BRAND_ACCESS_GUARD_FLAG = 'hermes.brandAccessGuard'

/**
 * RUM custom-action name emitted when the guard blocks a brand-scoped
 * navigation. Alert on this in Datadog (`@action.name:brand_access_guard.blocked`)
 * to catch foreign-brand deep links and routing bugs that send a non-brand
 * value (e.g. a route segment) as the `brandId`. The `source` attribute
 * distinguishes external entry (deep link / login redirect / bookmark) from
 * in-app navigation (which points at a routing/brand-picker bug).
 */
export const BRAND_ACCESS_BLOCKED_ACTION = 'brand_access_guard.blocked'

/**
 * Blocks navigation to a `/brands/:brandId/...` route when the authenticated
 * user has no access to that brand, and emits telemetry so the denial is
 * traceable in Datadog.
 *
 * Without this guard a foreign-brand deep link (e.g. a report email opened
 * under a different account) or a malformed `brandId` reaches `loadBrand`,
 * which fires `GET /core` and 403s — tripping the RBAC monitor. Catching it
 * here turns that into a clean redirect to the user's own home.
 *
 * Access is resolved against auth-service's `my-access` (`hasAccess`), the same
 * brand-reachability signal `/core` enforces. Cheap paths avoid any network:
 * routes without a brandId, public routes, in-brand navigation, super admins,
 * and pre-brand-session (embedded) states short-circuit. Only a cross-brand
 * entry on an auth-service session costs a lookup, which fails open so a
 * transient error never strands a legitimate switch. The cached
 * `isMultiBrandUser` flag is intentionally not used to gate — it can be stale.
 *
 * Runs after `checkAuthenticated`/`checkPermissions`, so the auth store is
 * populated.
 * @param to
 * @param from
 */
export const checkBrandAccess: HermesGuardFn = async (to, from) => {
  // Flag check first so the guard is fully gated: when disabled, none of the
  // logic below runs, so a toggle-off is a complete revert with no chance of
  // interacting with the short-circuit cases that follow. Cost is a flag
  // evaluation on every navigation. Disabled = legacy behavior (let
  // `loadBrand` reach the server and 403).
  if (!FeatureFlags.isEnabled(BRAND_ACCESS_GUARD_FLAG)) return undefined

  const brandId = to.params.brandId
  if (typeof brandId !== 'string' || !brandId) return undefined
  if (to.meta.isPublic) return undefined

  const authStore = useAuthStore()
  // checkAuthenticated owns the unauthenticated redirect; stay out of its way.
  if (!authStore.user) return undefined

  // Embedded/iframe sessions set `user` before `brandSession` (filled later by
  // loadBrand). With no active brand yet there's no baseline to compare and
  // `home` would resolve to the login route — so let loadBrand enforce access
  // rather than redirecting an authenticated user to login mid-handoff.
  if (!authStore.brandSession) return undefined

  // Super admins resolve permissions through an all-brands path — never block.
  if (authStore.isSuperAdmin) return undefined

  // Navigating within the already-loaded brand session is always allowed and
  // needs no lookup — this is the overwhelming majority of navigations.
  if (brandId === authStore.brandSession?.brandId) return undefined

  // Legacy Olympus session: no `my-access` equivalent to verify against, so
  // preserve existing behavior and let `loadBrand` enforce access server-side.
  if (authStore.sessionProvider !== 'auth-service') return undefined

  // Resolve access from my-access, the authoritative per-brand signal. We
  // deliberately do NOT short-circuit on the cached `isMultiBrandUser` flag:
  // it's written at login and goes stale if the user's brand access changes
  // mid-session, which would wrongly block a now-accessible brand. Cross-brand
  // navigations are rare, so the extra lookup is cheap.
  let accessible: boolean
  try {
    const { hasAccess } = await fetchMyBrandAccess({
      authServiceUrl: AUTH_SERVICE_URL,
      brandId,
      getCsrfToken: () => authStore.csrfToken,
    })
    accessible = hasAccess
  } catch (error) {
    // Fail open on a transient lookup failure: don't strand a legitimate
    // brand switch. `loadBrand` still enforces access server-side.
    logError(error, {
      attemptedBrandId: brandId,
      context: 'checkBrandAccess',
    })
    return undefined
  }

  if (accessible) return undefined

  logInfo(BRAND_ACCESS_BLOCKED_ACTION, {
    activeBrandId: authStore.brandSession?.brandId ?? null,
    attemptedBrandId: brandId,
    routeName: typeof to.name === 'string' ? to.name : null,
    source: from?.name ? 'in_app_navigation' : 'direct_or_redirect_entry',
  })

  return authStore.home
}
